import { useEffect, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { useRouter } from 'next/router';

import { Box, Button, Heading, Icon, Modal, Paragraph, Spinner, TextButton } from '@zillow/constellation-10';
import { IconPlusCircleFilled, IconZillowMark } from '@zillow/constellation-icons';

import { useRentalManagerUserPropertyPhotoWebApiHandler_generateUploadUrl } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import PhoneUploadPanel from './PhoneUploadPanel';
import PhoneUploadTile from './PhoneUploadTile';

import type { ListingAlias } from '@zg-rentals/ts-rental-manager-api-client';

const QR_SIZE_PX = 220;
/** Covers ~16% of the code's width, inside what level H can reconstruct. */
const QR_LOGO_PX = 36;

type Props = {
  isDisabled?: boolean;
  onUploadUrlGenerated?: () => void;
  /** `tile` sits in the photo grid; `panel` matches the dropzone shown when there are no photos yet. */
  variant?: 'tile' | 'panel';
};

const QrPhotoUploadModalTrigger = ({ isDisabled, onUploadUrlGenerated, variant = 'tile' }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as ListingAlias;

  const [isOpen, setIsOpen] = useState(false);

  const {
    data: uploadUrl,
    dataUpdatedAt,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useRentalManagerUserPropertyPhotoWebApiHandler_generateUploadUrl(
    { listingAlias },
    {
      enabled: isOpen && Boolean(listingAlias),
      staleTime: 0,
      gcTime: 0,
      meta: {
        tags: ['photoUploadUrl'],
        context: {
          api: 'rental-manager',
          details: 'useRentalManagerUserPropertyPhotoWebApiHandler_generateUploadUrl',
        },
      },
    },
  );

  const notifiedUploadUrlRef = useRef<string | null>(null);
  const openedAtMsRef = useRef<number | null>(null);

  if (isOpen && openedAtMsRef.current == null) {
    openedAtMsRef.current = Date.now();
  }
  if (!isOpen && openedAtMsRef.current != null) {
    openedAtMsRef.current = null;
  }

  useEffect(() => {
    const openedAtMs = openedAtMsRef.current;
    // Ignore a url cached by an earlier open cycle, so only a link the user can actually scan starts polling.
    const isFetchedForThisOpenCycle = openedAtMs != null && dataUpdatedAt >= openedAtMs;
    if (!isOpen || uploadUrl == null || !isFetchedForThisOpenCycle || notifiedUploadUrlRef.current === uploadUrl) {
      return;
    }
    notifiedUploadUrlRef.current = uploadUrl;
    onUploadUrlGenerated?.();
  }, [isOpen, uploadUrl, dataUpdatedAt, onUploadUrlGenerated]);

  const modal = (
    <Modal
      size={{ base: 'fullScreen', lg: 'md' }}
      dividers={false}
      open={isOpen}
      onOpenChange={setIsOpen}
      trigger={
        variant === 'panel' ? (
          <Button size="sm" icon={<IconPlusCircleFilled />} disabled={isDisabled} data-testid="qr-photo-upload-button">
            Scan QR code
          </Button>
        ) : (
          <PhoneUploadTile isDisabled={isDisabled} />
        )
      }
      header={<Heading level={2}>Add photos from your phone</Heading>}
      body={
        <>
          <Paragraph css={{ marginBottom: '600' }}>
            To use your phone to take and add photos to this listing, scan the QR code using your phone’s camera. A page
            will open where you can select existing photos or take new ones.
          </Paragraph>
          <Box css={{ display: 'flex', justifyContent: 'center', marginBottom: '600' }}>
            {isPending && <Spinner data-testid="qr-loading" />}
            {isError && (
              <Paragraph data-testid="qr-error" css={{ color: 'text.error' }}>
                We couldn’t create an upload link. Close this window and try again.
              </Paragraph>
            )}
            {!isError && uploadUrl != null && (
              <Box data-testid="qr-code" css={{ position: 'relative', backgroundColor: 'bg.neutral', padding: '400' }}>
                {/* Level H tolerates the centre mark covering modules; 220px keeps them big enough to scan. */}
                <QRCode value={uploadUrl} size={QR_SIZE_PX} level="H" />
                <Box
                  aria-hidden="true"
                  css={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'bg.neutral',
                    borderRadius: '200',
                  }}
                >
                  <Icon
                    render={<IconZillowMark />}
                    css={{ width: QR_LOGO_PX, height: QR_LOGO_PX, color: 'icon.accent.brand.hero' }}
                  />
                </Box>
              </Box>
            )}
          </Box>
          <Paragraph textStyle="body-sm" css={{ textAlign: 'center' }}>
            This code is valid for 30 minutes, and can be regenerated if it expires.{' '}
            <TextButton textStyle="body-sm" disabled={isFetching} onClick={() => refetch()}>
              Regenerate QR code
            </TextButton>
          </Paragraph>
        </>
      }
    />
  );

  return variant === 'panel' ? <PhoneUploadPanel isDisabled={isDisabled}>{modal}</PhoneUploadPanel> : modal;
};

export default QrPhotoUploadModalTrigger;
